import { useEffect, useMemo, useRef, useState } from "react";
import { BellOff, CheckCircle2, Loader2, MonitorSmartphone, ShieldCheck, Video } from "lucide-react";

// Plays the attached chime on the guest side whenever the LAKSMI status code changes.
// For "tidak_di_zoom" it rings 3 times (2s apart) and exposes a dismiss control.
export const useGuestChime = (code) => {
  const audio = useRef(null);
  const prev = useRef(null);
  const timers = useRef([]);
  const [dismissed, setDismissed] = useState(false);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const playOnce = () => {
    if (!audio.current) return;
    try { audio.current.currentTime = 0; audio.current.play().catch(() => {}); } catch (_) {}
  };

  useEffect(() => {
    if (!audio.current) audio.current = new Audio("/laksmi-guest-chime.mp3");
    const changed = code && prev.current && code !== prev.current;
    prev.current = code;
    if (!changed) return undefined;
    clearTimers();
    setDismissed(false);
    if (code === "tidak_di_zoom") {
      playOnce();
      timers.current.push(setTimeout(playOnce, 2000));
      timers.current.push(setTimeout(playOnce, 4000));
    } else {
      playOnce();
    }
    return undefined;
  }, [code]);

  useEffect(() => () => clearTimers(), []);

  const dismiss = () => {
    clearTimers();
    if (audio.current) { try { audio.current.pause(); audio.current.currentTime = 0; } catch (_) {} }
    setDismissed(true);
  };
  const showDismiss = code === "tidak_di_zoom" && !dismissed;
  return { showDismiss, dismiss };
};

export const LaksmiHero = ({ children }) => (
  <div className="laksmi-hero" data-testid="laksmi-hero">
    <div className="laksmi-hero-badge"><ShieldCheck size={14} /> LAKSMI · Layanan Konsultasi Kemahasiswaan</div>
    <h1 data-testid="laksmi-thankyou">Terima kasih telah menghubungi<br /><span>Tim Kerja Kemahasiswaan LLDIKTI Wilayah VII.</span></h1>
    {children}
  </div>
);

export const LaksmiInfo = ({ mode, message }) => (
  <div className={`laksmi-info laksmi-info-${mode}`} data-testid="laksmi-info-message"><p>{message}</p></div>
);

const Field = ({ label, value, onChange, placeholder, testid, textarea, note }) => (
  <label className="laksmi-field">
    <span>{label}</span>
    {textarea
      ? <textarea rows={3} value={value} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
      : <input value={value} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />}
    {note && value.trim() ? <em className="laksmi-note laksmi-note-blink" data-testid={`${testid}-note`}>{note}</em> : null}
  </label>
);

const LinkField = ({ label, value, onChange, testid, onCheckLink, onValidity }) => {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const v = value.trim();
    setResult(null);
    onValidity(false);
    if (!v || !onCheckLink) { setChecking(false); return undefined; }
    if (!/^https?:\/\//i.test(v)) {
      setChecking(false);
      setResult({ ok: false, reason: "Tautan harus diawali http:// atau https://" });
      return undefined;
    }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await onCheckLink(v);
        if (cancelled) return;
        setResult(r); onValidity(!!r.ok);
      } catch (_) {
        if (cancelled) return;
        setResult({ ok: false, reason: "Gagal memeriksa tautan." }); onValidity(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [value, onCheckLink, onValidity]);

  const invalid = !checking && result && !result.ok;
  return (
    <label className="laksmi-field">
      <span>{label}</span>
      <input className={invalid ? "laksmi-input-invalid" : ""} value={value} placeholder="https://..." data-testid={testid} onChange={(e) => onChange(e.target.value)} />
      {checking && <em className="laksmi-link-checking" data-testid={`${testid}-checking`}><Loader2 size={12} className="laksmi-spin" /> Memeriksa tautan…</em>}
      {invalid && <em className="laksmi-link-bad" data-testid={`${testid}-bad`}>✕ {result.reason}</em>}
      {invalid && <em className="laksmi-link-help" data-testid={`${testid}-help`}>Setel akses ke "Siapa saja yang memiliki link".</em>}
      {!checking && result && result.ok && <em className="laksmi-link-ok" data-testid={`${testid}-ok`}>✓ Tautan dapat dibaca publik</em>}
    </label>
  );
};

export const LaksmiJoinForm = ({ isLogin, submitLabel, busy, error, onSubmit, initial, onCheckLink }) => {
  const [f, setF] = useState(() => ({ nama: "", jabatan: "", topik: "", nama_zoom: "", nama_pt: "", doc_identitas_link: "", doc_pendukung_link: "", ...(initial || {}) }));
  const [chk1, setChk1] = useState(false);
  const [chk2, setChk2] = useState(false);
  const [link1Valid, setLink1Valid] = useState(false);
  const [link2Valid, setLink2Valid] = useState(false);
  const set = (k) => (v) => setF((c) => ({ ...c, [k]: v }));

  const coreFilled = f.nama.trim() && f.jabatan.trim() && f.topik.trim() && f.nama_zoom.trim();
  const linksOk = isLogin || (link1Valid && link2Valid);
  const publicFilled = isLogin || (f.nama_pt.trim() && f.doc_identitas_link.trim() && f.doc_pendukung_link.trim());
  const allFilled = coreFilled && publicFilled;
  const ready = allFilled && linksOk && chk1 && chk2;

  return (
    <form className="laksmi-form" data-testid="laksmi-join-form" onSubmit={(e) => { e.preventDefault(); if (ready) onSubmit(f); }}>
      <h3>Silahkan mengisi formulir buku tamu berikut :</h3>
      <Field label="Nama yang akan melaksanakan konsultasi" value={f.nama} onChange={set("nama")} testid="laksmi-nama" placeholder="Nama lengkap" />
      <Field label="Jabatan di Perguruan Tinggi" value={f.jabatan} onChange={set("jabatan")} testid="laksmi-jabatan" placeholder="Contoh: Operator KIP-K" />
      <Field label="Singkat apa yang ingin dikonsultasikan" value={f.topik} onChange={set("topik")} testid="laksmi-topik" textarea placeholder="Tuliskan ringkas topik konsultasi" />
      <Field label="Nama yang digunakan di Zoom" value={f.nama_zoom} onChange={set("nama_zoom")} testid="laksmi-nama-zoom" placeholder="Nama tampilan di Zoom" note="Pastikan nama ini Anda gunakan di zoom meeting." />

      {!isLogin && <>
        <div className="laksmi-divider">Data tambahan untuk verifikasi</div>
        <Field label="Nama Perguruan Tinggi" value={f.nama_pt} onChange={set("nama_pt")} testid="laksmi-nama-pt" placeholder="Nama perguruan tinggi" />
        <LinkField label="Tautan Dokumen KTP/SIM/Kartu Tanda Pengenal (wajib bisa dibaca publik)" value={f.doc_identitas_link} onChange={set("doc_identitas_link")} testid="laksmi-doc-identitas" onCheckLink={onCheckLink} onValidity={setLink1Valid} />
        <LinkField label="Tautan Surat Tugas / SK / Kartu Pegawai (wajib bisa dibaca publik)" value={f.doc_pendukung_link} onChange={set("doc_pendukung_link")} testid="laksmi-doc-pendukung" onCheckLink={onCheckLink} onValidity={setLink2Valid} />
        <p className="laksmi-hint">Setel berbagi tautan ke <strong>"Siapa saja yang memiliki link"</strong>. Pilih salah satu: 1) Surat tugas melaksanakan konsultasi LAKSMI (ttd Pimpinan PT, format bebas); 2) SK sebagai pejabat/operator/karyawan; atau Kartu Tanda Pegawai/Karyawan Perguruan Tinggi.</p>
      </>}

      {allFilled && <div className="laksmi-agree" data-testid="laksmi-agreement">
        <p className="laksmi-agree-title">Centang isian di bawah jika Anda setuju:</p>
        <label className="laksmi-check"><input type="checkbox" checked={chk1} data-testid="laksmi-check-1" onChange={(e) => setChk1(e.target.checked)} /><span>Anda akan menggunakan Nama <strong>{f.nama_zoom.trim()}</strong> pada saat zoom meeting.</span></label>
        <label className="laksmi-check"><input type="checkbox" checked={chk2} data-testid="laksmi-check-2" onChange={(e) => setChk2(e.target.checked)} /><span><MonitorSmartphone size={14} /> Anda wajib menggunakan Komputer dan bukan ponsel.</span></label>
      </div>}

      {error && <div className="laksmi-error" data-testid="laksmi-form-error">{error}</div>}
      <button type="submit" className="laksmi-primary" data-testid="laksmi-submit" disabled={!ready || busy}>
        {busy ? <Loader2 size={16} className="laksmi-spin" /> : <Video size={16} />} {submitLabel}
      </button>
    </form>
  );
};

const STATUS_TEXT = {
  waiting: "Menunggu di Waiting Room. Jangan keluar dari zoom. Admin mungkin sedang melayani tamu lainnya.",
  menunggu_admin: "Mohon menunggu. Kami segera melayani Anda.",
  selesai: "Terima kasih telah menghubungi kami.",
};

const PROGRESS_STAGES = ["Waiting Room", "Akan dilayani", "Dilayani", "Selesai"];

const ServiceProgress = ({ stage }) => (
  <div className="laksmi-progress" data-testid="laksmi-progress">
    {PROGRESS_STAGES.map((label, i) => {
      const n = i + 1;
      return (
        <div key={label} className={`laksmi-progress-step ${n <= stage ? "done" : ""} ${n === stage ? "current" : ""}`} data-testid={`laksmi-progress-step-${n}`}>
          <span className="laksmi-progress-dot" />
          <span className="laksmi-progress-label">{label}</span>
        </div>
      );
    })}
  </div>
);

export const LaksmiStatusOverlay = ({ status, showDismiss, onDismiss }) => {
  const code = status?.code;
  const nama = status?.nama_zoom || "";
  const stage = status?.stage;
  const isWaiting = code === "waiting";
  const [waitIdx, setWaitIdx] = useState(0);
  useEffect(() => {
    if (!isWaiting) { setWaitIdx(0); return undefined; }
    const t = setInterval(() => setWaitIdx((v) => (v + 1) % 3), 4500);
    return () => clearInterval(t);
  }, [isWaiting]);
  const body = useMemo(() => {
    if (code === "tidak_di_zoom") return <>Kami tidak menemukan Anda di zoom. Pastikan Anda sudah berada di zoom dengan Nama sesuai yang Anda input yaitu <strong>{nama}</strong> atau silahkan keluar dari zoom dan masuk lagi. Anda tidak perlu keluar dari halaman ini! Kami akan melayani Anda jika Nama di Zoom sudah sesuai. Jika mengalami kesulitan, telpon (031) 5925418 ekstensi 120.</>;
    if (stage === 3) return "Anda sedang dilayani.";
    if (isWaiting) {
      const msgs = [
        STATUS_TEXT.waiting,
        <>Pastikan Anda memakai nama <strong>{nama}</strong> di dalam Zoom Meeting agar kami dapat menemukan Anda di zoom.</>,
        <>Jika Anda tidak memakai nama <strong>{nama}</strong>, silahkan keluar dari zoom dan masuk kembali ke dalam zoom dengan menggunakan nama <strong>{nama}</strong>.</>,
      ];
      return msgs[waitIdx];
    }
    return STATUS_TEXT[code] || STATUS_TEXT.waiting;
  }, [code, nama, stage, isWaiting, waitIdx]);
  const done = code === "selesai";
  return (
    <div className="laksmi-overlay" data-testid="laksmi-status-overlay">
      <div className={`laksmi-overlay-card ${done ? "done" : ""} ${code === "tidak_di_zoom" ? "warn" : ""}`}>
        <div className="laksmi-overlay-brand" data-testid="laksmi-overlay-brand">
          <img src="/logo-padma-transparent.png" alt="Logo PADMA" />
          <strong>PADMA</strong>
          <span>Panel Layanan Administasi Kemahasiswaan</span>
        </div>
        <div className="laksmi-overlay-icon">{done ? <CheckCircle2 size={40} /> : <Loader2 size={40} className="laksmi-spin" />}</div>
        {stage ? <ServiceProgress stage={stage} /> : null}
        <h2>Status Anda</h2>
        <p data-testid="laksmi-status-text">{body}</p>
        {showDismiss && <button type="button" className="laksmi-primary laksmi-dismiss-chime" data-testid="laksmi-dismiss-chime" onClick={onDismiss}><BellOff size={16} /> Ok, mengerti!</button>}
      </div>
    </div>
  );
};
